/**
stats-overview
*/


<!--suppress JSUnfilteredForInLoop -->
<template>
    <div>
        <p class="chart-title">
            Charts showing how different areas fall within MPAs and byelaws restricting bottom towed fishing gear. Use the
            <span class="visible-lg-inline">checkboxes</span>
            <span class="visible-mobile-inline">dropdown</span>
            to add or remove areas of interest.
        </p>

        <div class="stats-mpas-sidebar visible-lg">
            <div v-for="area in availableAreas">
                <input type="checkbox" v-bind:id="'id' + area" v-bind:value="area" v-model="selectedAreas">
                <label v-bind:for="'id' + area">{{ area }}</label>
            </div>
        </div>

        <div class="mpas-and-byelaws-container">
            <div class="visible-mobile stats-mpas-dropdown">
                <div v-on:click.prevent="toggleDropdown"><span>Choose areas of interest</span> <font-awesome-icon v-bind:icon="dropdownIcon"/></div>

                <div id="stats-mpas-mobile-dropdown" class="dropdown-items" tabindex="1" v-if="dropdownVisible" v-on:blur="dropdownVisible = false">
                    <div v-for="area in availableAreas">
                        <input type="checkbox" v-bind:id="'id' + area" v-bind:value="area" v-model="selectedAreas">
                        <label v-bind:for="'id' + area">{{ area }}</label>
                    </div>
                </div>
            </div>

            <div id="mpas-and-byelaws"></div>

            <area-legend/>
        </div>


        <div class="clearfix"></div>
    </div>
</template>


<script>
import store from "../store/reality-check-store";
import AreaLegend from "./chart-union-area-legend.vue";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
library.add(faChevronUp, faChevronDown);


export default {
    name: "stats-mpas",
    components: {
        AreaLegend
    },
    store,
    props: {
        stateChange: {type: Number, required: true}  // to trigger redraw
    },
    data: function () {
        return {
            selectedAreas: [],
            chartData: [],
            initialLoad: true,
            dropdownVisible: false
        }
    },
    methods: {
        drawMpaChart: function () {
            if (this.initialLoad) {
                this.selectedAreas = [
                    "English territorial waters (MHW to 12NM)",
                    "MMO",
                    "Welsh territorial seas (MHW to 12NM)",
                    "Northern Irish territorial seas (MHW to 12NM)",
                    "Scottish territorial seas (MHW to 12NM)",
                    "Scottish offshore"
                ]
                this.initialLoad = false;
            }

            this.prepareData()

            const layout = {
                barmode: "stack", showlegend: false,
                autosize: true,
                height: (this.selectedAreas.length * 60),
                margin: {},
                title: "MPAs and byelaws",
                hovermode: "y",
                xaxis: {
                    title: 'km<sup>2</sup>',
                    ticksuffix: " km<sup>2</sup>",
                    tickformat: ',.0f',
                    showticksuffix: "last"
                },
                yaxis: {
                    autorange: "reversed",
                },
                font: {}
            }

            if (window.innerWidth >= 1023) {
                layout.margin = {t: 60, r: 0, b: 40, l: 180}
                layout.font = {size: 12}
            } else {
                layout.margin = {t: 60, r: 0, b: 40, l: 140}
                layout.font = {size: 10}
            }

            Plotly.newPlot("mpas-and-byelaws", this.chartData, layout, {responsive: true});
        },
        prepareData: function () {
            var x1 = [], x2 = [], x3 = [], x4 = [], y = []

            this.selectedAreas.forEach(function (area) {
                y.push(area.replace(/(?![^\n]{1,26}$)([^\n]{1,26})\s/g, '$1<br>'));  // https://stackoverflow.com/a/51506718/

                x1.push(this.mpasAndByelawsData[area]["area in mpa and byelaw"]);
                x2.push(this.mpasAndByelawsData[area]["area in mpa and not in byelaw"]);
                x3.push(this.mpasAndByelawsData[area]["area not in mpa and in byelaw"]);
                x4.push(this.mpasAndByelawsData[area]["area not in mpa and not in byelaw"]);
            }.bind(this))

            this.chartData = [
                {
                    "type": "bar",
                    "name": "Area in an MPA and protected by a byelaw restricting bottom towed fishing gear",
                    "x": x1,
                    "y": y,
                    "orientation": "h",
                    "hoverinfo": "x",
                    "marker": {
                        "color": "rgba(124, 184, 84, 1)"
                    }
                },
                {
                    "type": "bar",
                    "name": "Area in an MPA but not protected by a byelaw restricting bottom towed fishing gear",
                    "x": x2,
                    "y": y,
                    "orientation": "h",
                    "hoverinfo": "x",
                    "marker": {
                        "color": "rgba(242, 183, 1, 1)"
                    }
                },
                {
                    "type": "bar",
                    "name": "Area not in an MPA but protected by a byelaw restricting bottom towed fishing gear",
                    "x": x3,
                    "y": y,
                    "orientation": "h",
                    "hoverinfo": "x",
                    "marker": {
                        "color": "rgba(48, 93, 158, 1)"
                    }
                },
                {
                    "type": "bar",
                    "name": "Area not in an MPA or a byelaw restricting bottom towed fishing gear",
                    "x": x4,
                    "y": y,
                    "orientation": "h",
                    "hoverinfo": "x",
                    "marker": {
                        "color": "rgba(194, 195, 196, 1)"
                    }
                },
            ]
        },
        toggleDropdown: function () {
            this.dropdownVisible = !this.dropdownVisible;

            // note: to allow the div to get and release focus, need to set tabindex: https://stackoverflow.com/a/18504302
            this.$nextTick(function () {
                if (this.dropdownVisible) {
                    document.getElementById("stats-mpas-mobile-dropdown").focus()
                }
            })
        }
    },
    computed: {
        mpasAndByelawsData: function () {
            return this.$store.state.stats.mpasAndByelawsData
        },
        availableAreas: function () {
            if (!this.mpasAndByelawsData) {
                return []
            }
            return Object.keys(this.mpasAndByelawsData)
        },
        dropdownIcon: function () {
            if (this.dropdownVisible) {
                return ['fas', 'chevron-up']
            }
            return ['fas', 'chevron-down']
        }
    },
    watch: {
        selectedAreas: function () {
            this.drawMpaChart();
        },
        stateChange: function () {
            this.drawMpaChart();
        }
    },
    mounted: function () {}
}
</script>


<style lang="scss">
    @import "../../../styles/variables.scss";

    .stats-mpas-sidebar {
        width: 250px;
        float: left;
        font-size: 80%;

        &div {
            margin-bottom: 5px;
        }

        input {
            float: left;
            margin: 4px 6px 0 0;
        }
        label {

            overflow: hidden;
            display: block;
        }
    }

    .mpas-and-byelaws-container {
        width: 100%;

        @media #{$breakpoint-lg} {
            width: calc(100% - 265px);
            float: right;
        }
    }

    .stats-mpas-dropdown {
        position: relative;

        > div {
            display: inline-block;
            cursor: pointer;
            border: 1px solid grey;
            border-radius: 5px;
            padding: 4px 6px 4px 4px;
            background-color: #E9E9ED;

            span {
                margin-right: 5px;
            }
        }
        margin-bottom: 5px;

        .dropdown-items {
            position: absolute;
            z-index: 9999;
            left: 0;
            top: 33px;

            background-color: #F9F9FB;
            user-select: none;
            cursor: pointer;
        }
    }

</style>